<script>
    import { onMount } from "svelte";
    import HeroWebGL from "./HeroWebGL.svelte";
    import ContentCarousel from "../../components/ContentCarousel.svelte";
    import allContent from "../../data/content.json";

    const mathContent = allContent.filter(item => item.tags.includes("#math"));
    const scienceContent = allContent.filter(item => item.tags.includes("#science"));
    const csContent = allContent.filter(item => item.tags.includes("#cs"));

    let blogSection;

    function scrollToBlog() {
        blogSection && blogSection.scrollIntoView({ behavior: "smooth" });
    }

    // Custom action for fading in elements cleanly when scrolled
    function fadeIn(node) {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        node.classList.add("visible");
                        observer.unobserve(node);
                    }
                });
            },
            { threshold: 0.05, rootMargin: "0px 0px -40px 0px" }
        );
        observer.observe(node);
        return {
            destroy() {
                observer.disconnect();
            }
        };
    }
</script>

<div class="home-redesign">
    <!-- ════════════════════════════════════════════
         SECTION 1: INTERACTIVE HERO
         ════════════════════════════════════════════ -->
    <HeroWebGL onScrollHint={scrollToBlog} />

    <!-- ════════════════════════════════════════════
         SECTION 2: THREE LAYER CAROUSELS
         ════════════════════════════════════════════ -->
    <section class="blog-section" bind:this={blogSection} id="blog-section">
        <div class="archive-inner">
            <!-- Archive Header -->
            <header class="archive-header fade-item" use:fadeIn>
                <h1 class="archive-title">Archive</h1>
                <p class="archive-subtitle">수학, 자연 과학, 컴퓨터 과학에 대한 지식 기록</p>
            </header>

            <div class="carousel-list">
                <!-- 1단: 수학 -->
                <div class="carousel-wrapper fade-item" use:fadeIn>
                    <ContentCarousel
                        title="수학"
                        subtitle="정의와 구조를 중심으로 수학 개념을 읽는 컬렉션"
                        contentItems={mathContent}
                    />
                </div>

                <!-- 2단: 자연 과학 -->
                <div class="carousel-wrapper fade-item" use:fadeIn>
                    <ContentCarousel
                        title="자연 과학"
                        subtitle="현상을 설명하는 자연 과학 개념을 하나의 흐름으로 묶었습니다."
                        contentItems={scienceContent}
                    />
                </div>

                <!-- 3단: 컴퓨터 과학 -->
                <div class="carousel-wrapper fade-item" use:fadeIn>
                    <ContentCarousel
                        title="컴퓨터 과학"
                        subtitle="설명형 글과 실험형 도구를 함께 배치한 컴퓨터 과학 컬렉션"
                        contentItems={csContent}
                    />
                </div>
            </div>
        </div>
    </section>
</div>

<style>
    .home-redesign {
        width: 100%;
        background-color: var(--bg-color);
    }

    /* ═══════════════════════════════════════
       SECTION 2: ARCHIVE
       ═══════════════════════════════════════ */
    .blog-section {
        background: var(--bg-color);
        min-height: 100vh;
        width: 100%;
        padding: var(--spacing-xxl) var(--spacing-lg) 100px;
        position: relative;
    }

    .archive-inner {
        max-width: 1280px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xl);
    }

    /* ── Archive Header ── */
    .archive-header {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
        margin-bottom: var(--spacing-lg);
    }

    .archive-title {
        font-family: var(--font-sans);
        font-size: clamp(2.5rem, 5vw, 4rem);
        font-weight: 700;
        letter-spacing: -0.04em;
        line-height: 1;
        color: var(--text);
    }

    .archive-subtitle {
        font-family: var(--font-sans);
        font-size: 1.1rem;
        font-weight: 400;
        color: var(--muted);
        line-height: 1.6;
        max-width: 600px;
    }

    .carousel-list {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xxl);
    }

    .carousel-wrapper {
        border-top: 1px solid var(--line);
        padding-top: var(--spacing-xl);
    }

    /* ── Fade-in Animation ── */
    .fade-item {
        opacity: 0;
        transform: translateY(20px);
        transition: opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1),
                    transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
    }

    :global(.fade-item.visible) {
        opacity: 1;
        transform: translateY(0);
    }

    @media (max-width: 900px) {
        .blog-section {
            padding: var(--spacing-xl) var(--spacing-md) 72px;
        }

        .archive-inner {
            gap: var(--spacing-lg);
        }

        .carousel-list {
            gap: var(--spacing-xl);
        }
    }
</style>
